/**
 * useAutoScroll Hook
 * 
 * Provides auto-scrolling functionality for kiosk displays.
 * Smoothly scrolls content up and down with pauses at top/bottom.
 * 
 * Features:
 * - 5 second pause at top and bottom of scroll
 * - Smooth ~25fps scrolling animation
 * - Automatically disables when content fits on screen
 * 
 * @module kiosk/hooks/useAutoScroll
 */

import React, { useEffect } from 'react';

/**
 * Auto-scrolls a container element up and down.
 * 
 * @param ref - React ref to the scrollable container
 * @param dependency - Dependency to re-initialize scroll (e.g., data array)
 */
export const useAutoScroll = (
    ref: React.RefObject<HTMLDivElement>,
    dependency: unknown
): void => {
    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        let intervalId: NodeJS.Timeout;
        let timeoutId: NodeJS.Timeout;
        let direction = 1; // 1 = down, -1 = up
        let isPaused = false;

        // Scroll configuration
        const scrollAmount = 1; // pixels per step
        const stepTime = 40; // milliseconds per step (~25fps)
        const pauseDuration = 5000; // ms to pause at top/bottom

        const scroll = (): void => {
            if (!element || isPaused) return;

            const { scrollTop, scrollHeight, clientHeight } = element;

            // If content fits on screen, no scrolling needed
            if (scrollHeight <= clientHeight) return;

            // Check if we reached the bottom
            if (direction === 1 && Math.ceil(scrollTop + clientHeight) >= scrollHeight) {
                isPaused = true;
                timeoutId = setTimeout(() => {
                    direction = -1;
                    isPaused = false;
                }, pauseDuration);
                return;
            }

            // Check if we reached the top
            if (direction === -1 && scrollTop <= 0) {
                isPaused = true;
                timeoutId = setTimeout(() => {
                    direction = 1;
                    isPaused = false;
                }, pauseDuration);
                return;
            }

            // Perform scroll
            element.scrollTop += direction * scrollAmount;
        };

        // Start the scroll interval
        intervalId = setInterval(scroll, stepTime);

        // Cleanup on unmount or dependency change
        return () => {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };
    }, [ref, dependency]);
};
